// CookiePolicy.jsx

import React, { useEffect } from "react";
import {
  ArrowLeft,
  ShieldCheck,
  Cookie,
  Globe,
  BarChart3,
  Settings2,
  Phone,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CookiePolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, []);

  const sections = [
  {
    icon: <Cookie size={22} />,
    title: "Use of Cookies and Similar Technologies",
    content: (
      <div className="space-y-6">
        <p>
         Drivault uses cookies and similar technologies to
        operate, secure, improve, and enhance our services
        and user experience.
        </p>

        <p>
          A cookie is a small data file sent to your browser when you
          visit a website. We use our own cookies as well as third-party
          cookies. See the “How does Drivault use cookies?” section below
          for information on third-party cookies we use.
        </p>

        <p>
          In addition to cookies, we use similar technologies, such as
          pixel tags ("pixels"), which are small pieces of code belonging
          to a third party that can be embedded on websites and emails.
          Another example of a similar technology is local storage, which
          allows us to store information locally on your browser or device
          to improve your experience, such as remembering your settings
          and preferences.
        </p>

        <p>
          <span className="font-semibold">Important:</span> For the
          purposes of this policy, all of these technologies are referred
          to as “cookies.”
        </p>
      </div>
    ),
  },

  {
    icon: <ShieldCheck size={22} />,
    title: "How Does Drivault Use Cookies?",
    content: (
      <div className="space-y-6">
  <p>
    Drivault uses different categories of cookies to
    provide, operate, secure, improve, and enhance
    our websites and services.
  </p>

  <p>
    Drivault retains personal information only for
    as long as required to provide services, comply
    with legal obligations, resolve disputes, and
    enforce agreements.
  </p>

  <p>
    The categories of cookies and their purposes are
    described below.
  </p>
</div>
    ),
  },

  {
    icon: <ShieldCheck size={22} />,
    title: "Strictly Necessary Cookies",
    content: (
      <div className="space-y-6">

        <p>
          These are cookies that are essential in order to operate the
          website and services.
        </p>

        <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 space-y-5">

          <div>
            <h4 className="font-semibold text-lg mb-2">
              Category
            </h4>

            <p className="text-slate-600">
              Strictly Necessary
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2">
              Purpose
            </h4>

            <p className="text-slate-600">
              Cookies that are used to operate the website and services,
              such as to log you in and keep your account secure.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2">
              Data Elements
            </h4>

            <p className="text-slate-600">
              Categories of data collected include authentication data,
              unique ID, browser information, and language settings.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2">
              Expiration
            </h4>

            <p className="text-slate-600">
              These cookies may expire after periods ranging from
              30 days to 5 years.
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-2">
              Third Parties
            </h4>

            <p className="text-slate-600">
              Third-party providers may include Google and cloud
              infrastructure providers.
            </p>
          </div>

        </div>
      </div>
    ),
  },
  {
  icon: <Settings2 size={22} />,
  title: "Consent to Cookies",
  content: (
    <div className="space-y-6">

      <p>
        By accessing or using Drivault websites and
        services, you consent to the use of cookies
        and similar technologies as described in this
        Cookie Policy.
      </p>

      <p>
        You may disable cookies through your browser
        settings at any time. However, disabling
        certain cookies may affect the functionality,
        performance, or availability of some services.
      </p>

    </div>
  ),
},

  {
    icon: <BarChart3 size={22} />,
    title: "Non-Essential Cookies",
    content: (
      <div className="space-y-8">

        {/* Analytics */}
        <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 space-y-4">

          <h3 className="text-xl font-bold">
            Analytics
          </h3>

          <p className="text-slate-600">
            Cookies that help us and our partners understand how our
            websites are engaged with, such as usage statistics and
            interaction with UI elements, in order to improve and
            customize our services.
          </p>

          <p className="text-slate-600">
            Data collected may include website usage data, number of
            visits, browser and device information, and unique IDs.
          </p>

          <p className="text-slate-600">
            These cookies may expire from session duration
            (~20 minutes) up to 2 years.
          </p>

          <p className="text-slate-600">
            Third-party providers may include Google Analytics and
            similar analytics providers.
          </p>

        </div>

        {/* Marketing */}
        <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 space-y-4">

          <h3 className="text-xl font-bold">
            General Marketing and Advertising
          </h3>

          <p className="text-slate-600">
            Cookies that help Drivault and our partners understand user
            interests and improve advertisements, campaigns, and
            promotional communications.
          </p>

          <p className="text-slate-600">
            Data collected may include browser information, advertisement
            interaction data, version data, and referral information.
          </p>

          <p className="text-slate-600">
            These cookies may expire from session duration up to
            2 years.
          </p>

          <p className="text-slate-600">
            Third-party providers may include Google and advertising
            partners.
          </p>

        </div>

        {/* Performance */}
        <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 space-y-4">

          <h3 className="text-xl font-bold">
            Performance and Functionality
          </h3>

          <p className="text-slate-600">
            Cookies that help us remember your preferences and settings
            in order to improve your experience while using Drivault
            services.
          </p>

          <p className="text-slate-600">
            Data collected may include website usage data, preferences,
            email address, and unique IDs.
          </p>

          <p className="text-slate-600">
            These cookies may expire from session duration up to
            3 years.
          </p>

          <p className="text-slate-600">
            Third-party providers may include customer support and live
            chat platforms.
          </p>

        </div>

        {/* Social */}
        <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6 space-y-4">

          <h3 className="text-xl font-bold">
            Social Media Advertising
          </h3>

          <p className="text-slate-600">
            Cookies used by Drivault and partners to understand the
            effectiveness of advertisements on social media services and
            provide advertisements relevant to your interests.
          </p>

          <p className="text-slate-600">
            Data collected may include browser data, device information,
            advertisement conversion data, website activity data, and
            unique IDs.
          </p>

          <p className="text-slate-600">
            These cookies may expire from session duration up to
            2 years.
          </p>

          <p className="text-slate-600">
            Third-party providers may include analytics,
            advertising, customer support, and cloud
            infrastructure service providers.
          </p>

        </div>

      </div>
    ),
  },

  {
    icon: <Globe size={22} />,
    title: "How Can I Control the Data Collected Through Cookies?",
    content: (
      <div className="space-y-8">

        <div>
          <h3 className="text-xl font-bold mb-3">
            Browser Settings
          </h3>

          <p className="text-slate-600 leading-7">
            Drivault allows users to manage cookie preferences
through browser settings and available cookie controls
provided on our websites. You may configure your browser to reject cookies,
            though doing so may limit your ability to use certain
            Drivault services.
          </p>

        </div>

        <div>
          <h3 className="text-xl font-bold mb-3">
            Cookie Preferences
          </h3>

          <p className="text-slate-600 leading-7">
            When using our services, you may choose which cookies you
            consent to through cookie preference settings available on
            our website.
          </p>
        </div>

        <div>
  <h3 className="text-xl font-bold mb-3">
    Indian Privacy & Data Protection Rights
  </h3>

  <p className="text-slate-600 leading-7">
    Drivault processes personal information in accordance
    with applicable Indian laws, including the Digital
    Personal Data Protection Act, 2023 ("DPDP Act")
    and the Information Technology Act, 2000 along with
    applicable rules and regulations.
  </p>

  <p className="text-slate-600 leading-7 mt-4">
    Users may request access, correction, or deletion
    of their personal information by contacting
    support@drivault.com.
  </p>

  <p className="text-slate-600 leading-7 mt-4">
    Drivault does not sell personal information to
    third parties. Cookies and similar technologies
    are used only for operational, security,
    analytics, and service improvement purposes.
  </p>

  <p className="text-slate-600 leading-7 mt-4">
    By continuing to use our websites and services,
    you consent to the use of cookies as described
    in this Cookie Policy.
  </p>
</div>

      </div>
    ),
  },
];

  return (
    <div className="min-h-screen bg-[#F6F9FC] text-[#0F172A] overflow-hidden">

      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-[#40E095]/10 via-[#40E095]/5 to-transparent pointer-events-none" />

      <div className="absolute top-40 right-[0px] w-[350px] h-[350px] bg-[#40E095]/10 blur-3xl rounded-full pointer-events-none" />

      <div className="absolute bottom-0 left-[-120px] w-[350px] h-[350px] bg-emerald-200/20 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 py-14">

        {/* Back Button */}
        <button
          onClick={() => {
            navigate("/");

            setTimeout(() => {
              window.scrollTo({
                top: 0,
                behavior: "instant",
              });
            }, 0);
          }}
          className="flex items-center gap-2 text-gray-500 hover:text-[#40E095] transition-all duration-300 mb-10"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">
            Back
          </span>
        </button>

        {/* HERO */}
        <div className="relative bg-white/90 backdrop-blur-xl border border-white shadow-[0_10px_40px_rgba(0,0,0,0.05)] rounded-[28px] overflow-hidden mb-6">

          <div className="absolute top-0 right-0 w-[180px] h-[180px] bg-[#40E095]/10 blur-3xl rounded-full" />

          <div className="relative p-5 md:p-8">

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

              {/* Left */}
              <div className="max-w-3xl">

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Cookie{" "}
                  <span className="text-[#40E095]">
                    Policy
                  </span>
                </h1>

                <p className="mt-4 text-base md:text-lg text-slate-600 leading-7 max-w-3xl">
                  This Cookie Policy explains how Drivault uses cookies and
                  similar technologies to improve security, functionality,
                  analytics, marketing, and your overall experience across our
                  websites and services.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mt-6">

                  <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-4">
                    <p className="text-xs text-slate-500 mb-1">
                      Posted
                    </p>

                    <p className="font-semibold text-base">
                      May 9, 2026
                    </p>
                  </div>

                  <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-4">
                    <p className="text-xs text-slate-500 mb-1">
                      Effective
                    </p>

                    <p className="font-semibold text-base">
                      May 9, 2026
                    </p>
                  </div>

                </div>
              </div>

              {/* Right Card */}
              <div className="w-full lg:max-w-[320px]">

                <div className="bg-[#0F172A] text-white rounded-3xl p-6 shadow-xl relative overflow-hidden">

                  <div className="absolute top-0 right-0 w-28 h-28 bg-[#40E095]/20 blur-3xl rounded-full" />

                  <div className="relative">

                    <div className="w-14 h-14 rounded-2xl bg-[#40E095]/15 border border-[#40E095]/30 flex items-center justify-center mb-4">
                      <ShieldCheck
                        size={28}
                        className="text-[#40E095]"
                      />
                    </div>

                    <h3 className="text-xl font-bold mb-3">
                      Drivault
                    </h3>

                    <div className="space-y-3 text-slate-300 leading-6 text-sm">

                      <p>
                        Outofbox Cloud Private Limited
                      </p>

                      <p>
                        4th Floor, Oneness,
                        Sadashivnagar, Belagavi,
                        Karnataka, India 590001
                      </p>

                      <div className="pt-3 border-t border-white/10">

                        <div className="flex items-center gap-2 mb-2">
                          <Phone size={15} />
                          <span>
                            6363-900-800
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <Mail size={15} />
                          <span className="break-all">
                            support@drivault.com
                          </span>
                        </div>

                      </div>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="grid gap-8">
          {sections.map((section, index) => (
            <div
              key={index}
              className="group bg-white/90 backdrop-blur-xl border border-white shadow-[0_10px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_60px_rgba(64,224,149,0.12)] transition-all duration-500 rounded-[28px] p-8 md:p-10"
            >
              <div className="flex items-start gap-5">

                <div className="min-w-[60px] w-[60px] h-[60px] rounded-2xl bg-[#40E095]/10 border border-[#40E095]/20 flex items-center justify-center text-[#40E095] group-hover:scale-110 transition-transform duration-300">
                  {section.icon}
                </div>

                <div>

                  <h2 className="text-2xl md:text-3xl font-bold mb-4 tracking-tight">
                    {section.title}
                  </h2>

                 <div className="text-slate-600 leading-7 text-[17px]">
                    {section.content}
                    </div>

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        {/* <div className="mt-10 bg-gradient-to-r from-[#0F172A] to-[#111827] rounded-[28px] p-7 md:p-10 text-white relative overflow-hidden">

          <div className="absolute top-0 right-0 w-[220px] h-[220px] bg-[#40E095]/20 blur-3xl rounded-full" />

          <div className="relative z-10">

            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck
                className="text-[#40E095]"
                size={28}
              />

              <h2 className="text-2xl md:text-3xl font-bold">
                Contact Us
              </h2>
            </div>

            <p className="text-slate-300 text-base leading-7 max-w-3xl">
              If you have any questions regarding this Cookie Policy or how
              Drivault uses cookies and related technologies, please contact our
              support team.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mt-7">

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <p className="text-slate-400 text-sm mb-1">
                  Customer Care
                </p>

                <p className="text-lg font-semibold">
                  6363-900-800
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <p className="text-slate-400 text-sm mb-1">
                  Email Address
                </p>

                <p className="text-lg font-semibold break-all">
                  support@drivault.com
                </p>
              </div>

            </div>

            <div className="mt-7 pt-5 border-t border-white/10 text-slate-400 text-sm">
              © 2026 Drivault — Outofbox Cloud Private Limited.
              All rights reserved.
            </div>

          </div>
        </div> */}

      </div>
    </div>
  );
};

export default CookiePolicy;